import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Sparkles, Play, Zap, Shield, Gauge, Search, Rocket, Database,
  Layout, Server, Palette, Route, Bug, FlaskConical, Lightbulb, Check,
  ChevronDown, Terminal, Code2, Eye, Wand2,
} from "lucide-react";
import { Logo } from "../components/Logo";
import { useAuth } from "../context/AuthContext";

const AGENTS = [
  { icon: Lightbulb, name: "Product", color: "text-amber-400" },
  { icon: Route, name: "UX", color: "text-cyan-400" },
  { icon: Palette, name: "UI", color: "text-pink-400" },
  { icon: Layout, name: "Frontend", color: "text-indigo-400" },
  { icon: Server, name: "Backend", color: "text-emerald-400" },
  { icon: Database, name: "Database", color: "text-violet-400" },
  { icon: Shield, name: "Security", color: "text-rose-400" },
  { icon: FlaskConical, name: "Testing", color: "text-sky-400" },
  { icon: Bug, name: "Debug", color: "text-orange-400" },
  { icon: Gauge, name: "Performance", color: "text-lime-400" },
  { icon: Search, name: "SEO", color: "text-teal-400" },
  { icon: Rocket, name: "Deploy", color: "text-fuchsia-400" },
];

const STEPS = ["Planning", "Designing", "Architecting", "Coding", "Testing", "Deploying"];

const SUGGESTIONS = [
  { label: "AI SaaS dashboard", icon: Layout, prompt: "Build a modern AI SaaS analytics dashboard with authentication, charts, and a Stripe billing page" },
  { label: "AI chatbot app", icon: Sparkles, prompt: "Build an AI chatbot app with streaming responses, saved chat history, and a clean dark UI" },
  { label: "E-commerce store", icon: Rocket, prompt: "Build an e-commerce store with a product grid, cart, checkout, and Stripe payments" },
  { label: "Booking platform", icon: Database, prompt: "Build a modern AI-powered appointment booking platform for dentists with a calendar and reminders" },
];

const FEATURES = [
  { icon: Sparkles, title: "Idea to App", desc: "Describe it in plain English. NEXORA plans, designs and builds a real working application — not a mockup.", span: "lg:col-span-7" },
  { icon: Bug, title: "Autonomous Debugging", desc: "Build → Test → Detect → Fix → Retest. Errors get resolved automatically.", span: "lg:col-span-5" },
  { icon: Code2, title: "Code + Design + AI modes", desc: "Switch between visual editing, a real code editor, and natural-language commands.", span: "lg:col-span-5" },
  { icon: Eye, title: "Live Preview", desc: "See your app run instantly across desktop, tablet and mobile viewports.", span: "lg:col-span-7" },
  { icon: Shield, title: "Security & Health Scores", desc: "Every build ships with a security audit, performance, SEO and accessibility score.", span: "lg:col-span-4" },
  { icon: Database, title: "Visual Database", desc: "AI designs your schema. Tables, fields and relationships, generated for you.", span: "lg:col-span-4" },
  { icon: Rocket, title: "One-Click Deploy", desc: "Building → Testing → Deploying → Live. Custom domains and rollback included.", span: "lg:col-span-4" },
];

const TEMPLATES = [
  { name: "SaaS Platform", tag: "React · FastAPI", accent: "from-indigo-500/20" },
  { name: "AI Chatbot", tag: "Streaming · LLM", accent: "from-cyan-500/20" },
  { name: "E-commerce Store", tag: "Stripe · Cart", accent: "from-emerald-500/20" },
  { name: "CRM Dashboard", tag: "Analytics", accent: "from-violet-500/20" },
  { name: "Booking System", tag: "Calendar · Auth", accent: "from-amber-500/20" },
  { name: "Marketplace", tag: "Multi-vendor", accent: "from-rose-500/20" },
];

const FAQS = [
  { q: "Is the generated code really mine?", a: "Yes. Every project is real HTML/CSS/JS you can read in the code editor, edit, and restore across versions — no lock-in." },
  { q: "How does the AI keep context of my project?", a: "NEXORA keeps your project's files and history as memory, so a prompt like 'make it more premium' updates the existing design rather than starting over." },
  { q: "Do I need to know how to code?", a: "No — describe changes in plain English and NEXORA edits the app for you. Open the built-in code editor whenever you want full control." },
  { q: "Which AI models can I use?", a: "You choose per build and per edit: Claude Sonnet 4.6 / Opus 4.6, or OpenAI GPT-5.4 / 5.4-mini / 5.6. Deeper third-party service integrations are on the roadmap." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [idea, setIdea] = useState("");
  const [stepIdx, setStepIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setStepIdx((i) => (i + 1) % STEPS.length), 1400);
    return () => clearInterval(t);
  }, []);

  const start = () => {
    if (idea.trim()) localStorage.setItem("nexora_pending_idea", idea.trim());
    navigate(user ? "/dashboard" : "/signup");
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex items-center gap-8 text-sm text-slate-400">
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            <a href="#agents" className="hover:text-white transition-colors">Agents</a>
            <a href="#templates" className="hover:text-white transition-colors">Templates</a>
            <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
          </nav>
          <div className="flex items-center gap-3">
            <span className="hidden sm:flex items-center gap-2 font-mono text-xs text-emerald-400">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse-glow" /> v2.4 Live
            </span>
            <button data-testid="nav-login-button" onClick={() => navigate("/login")} className="text-sm text-slate-300 hover:text-white transition-colors">Sign in</button>
            <button data-testid="nav-signup-button" onClick={() => navigate("/signup")} className="text-sm font-semibold px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 transition-colors">Start Building Free</button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="absolute inset-0 radial-fade" />
        <div className="relative max-w-4xl mx-auto px-6 pt-24 pb-20 text-center">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mb-8 text-xs font-mono text-indigo-300">
            <Sparkles className="h-3.5 w-3.5" /> An AI engineering team in your browser
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08]">
            Build Software at the<br /><span className="gradient-text">Speed of Thought</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12 }}
            className="mt-6 text-base sm:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Turn an idea into a production-ready website or application using an AI engineering team that plans, builds, tests, debugs, and deploys for you.
          </motion.p>

          {/* Prompt bar */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-10 max-w-2xl mx-auto">
            <div className="prompt-shell">
              <div className="prompt-glow" />
              <div className="relative z-[1] glass rounded-[1.25rem] p-2 text-left">
                <div className="flex items-center gap-2 px-3 pt-2 text-xs font-mono text-slate-500">
                  <Terminal className="h-3.5 w-3.5" /> What do you want to build?
                </div>
                <textarea
                  data-testid="hero-idea-input"
                  value={idea}
                  onChange={(e) => setIdea(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) start(); }}
                  placeholder="Build me a modern AI-powered appointment booking platform for dentists…"
                  rows={3}
                  className="w-full bg-transparent resize-none outline-none px-3 py-2 text-white placeholder:text-slate-600"
                />
                <div className="flex items-center justify-between px-2 pb-1 gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="flex items-center gap-1.5 shrink-0 text-[11px] font-mono px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 text-indigo-300">
                      <Wand2 className="h-3 w-3" /> Autonomous multi-agent
                    </span>
                    <div className="hidden sm:flex items-center gap-2 text-xs font-mono text-slate-500 h-6 overflow-hidden">
                      <span className="h-1.5 w-1.5 rounded-full bg-indigo-400 animate-pulse-glow" />
                      <motion.span key={stepIdx} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="text-indigo-300">
                        {STEPS[stepIdx]}…
                      </motion.span>
                    </div>
                  </div>
                  <button data-testid="hero-start-building-button" onClick={start}
                    className="flex items-center gap-2 shrink-0 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 transition-colors font-semibold text-sm">
                    Start Building <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Prompt suggestions */}
            <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
              {SUGGESTIONS.map((s) => (
                <button key={s.label} data-testid={`hero-suggestion-${s.label.toLowerCase().replace(/ /g, "-")}`}
                  onClick={() => setIdea(s.prompt)}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full glass text-slate-400 hover:text-white transition-colors card-hover">
                  <s.icon className="h-3.5 w-3.5 text-indigo-400" /> {s.label}
                </button>
              ))}
            </div>

            <div className="flex items-center justify-center gap-4 mt-5">
              <button data-testid="hero-watch-demo-button" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors">
                <Play className="h-4 w-4" /> Watch demo
              </button>
              <span className="text-slate-600">·</span>
              <span className="text-sm text-slate-400">Free to start · No card required</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Agents */}
      <section id="agents" className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-14">
          <p className="caption text-xs font-mono tracking-widest uppercase text-indigo-400 mb-3">Multi-agent system</p>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">Twelve specialists. One conversation.</h2>
          <p className="text-slate-400 mt-4 max-w-2xl mx-auto">Each agent owns a discipline and shares a single project context — coordinating like a real engineering org.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {AGENTS.map((a, i) => (
            <motion.div key={a.name} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.03 }}
              data-testid={`agent-card-${a.name.toLowerCase()}`}
              className="glass rounded-xl p-4 card-hover flex flex-col items-center text-center gap-2">
              <div className="h-10 w-10 rounded-lg bg-white/[0.04] flex items-center justify-center">
                <a.icon className={`h-5 w-5 ${a.color}`} />
              </div>
              <span className="text-sm font-medium">{a.name}</span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Features bento */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-14">
          <p className="text-xs font-mono tracking-widest uppercase text-indigo-400 mb-3">Everything included</p>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">A complete platform, not a demo</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {FEATURES.map((f) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className={`glass rounded-2xl p-7 card-hover ${f.span}`}>
              <div className="h-11 w-11 rounded-xl bg-indigo-500/10 flex items-center justify-center mb-5">
                <f.icon className="h-5 w-5 text-indigo-400" />
              </div>
              <h3 className="font-heading text-xl font-semibold mb-2">{f.title}</h3>
              <p className="text-slate-400 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Templates */}
      <section id="templates" className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="text-xs font-mono tracking-widest uppercase text-indigo-400 mb-3">Start faster</p>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">Templates, customized to your business</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {TEMPLATES.map((t) => (
            <div key={t.name} data-testid={`template-card-${t.name.toLowerCase().replace(/ /g, "-")}`}
              onClick={() => { localStorage.setItem("nexora_pending_idea", `Build a ${t.name} application`); navigate(user ? "/dashboard" : "/signup"); }}
              className={`relative glass rounded-2xl p-6 h-40 flex flex-col justify-between card-hover cursor-pointer overflow-hidden bg-gradient-to-br ${t.accent} to-transparent`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs text-slate-400">{t.tag}</span>
                <ArrowRight className="h-4 w-4 text-slate-500" />
              </div>
              <h3 className="font-heading text-xl font-semibold">{t.name}</h3>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <PricingPreview navigate={navigate} user={user} />

      {/* Honest positioning — no fabricated social proof */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-14">
          <p className="text-xs font-mono tracking-widest uppercase text-indigo-400 mb-3">Built to be honest</p>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">A new platform — here's the real deal</h2>
          <p className="text-slate-400 mt-4 max-w-2xl mx-auto">NEXORA is early. We'd rather earn your trust than fake it, so here's exactly what you get today.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { icon: Eye, title: "Working front-end, instantly", desc: "Describe an idea and get a real, interactive front-end you can preview, edit by chat, and export — no dead buttons." },
            { icon: Code2, title: "You own the code", desc: "Every project is real HTML/CSS/JS you can read, edit in-browser, restore across versions, and take with you." },
            { icon: Shield, title: "No fake numbers", desc: "Your dashboard shows only your real projects and deploys. Server-backed features are clearly labelled when they're a demo." },
          ].map((f) => (
            <div key={f.title} className="glass rounded-2xl p-6 card-hover">
              <div className="h-11 w-11 rounded-xl bg-indigo-500/10 flex items-center justify-center mb-5"><f.icon className="h-5 w-5 text-indigo-400" /></div>
              <h3 className="font-heading text-lg font-semibold mb-2">{f.title}</h3>
              <p className="text-slate-400 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 py-20">
        <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight text-center mb-12">Frequently asked</h2>
        <div className="space-y-3">
          {FAQS.map((f, i) => <FaqItem key={i} {...f} />)}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="glass rounded-3xl p-12 lg:p-16 text-center relative overflow-hidden">
          <div className="absolute inset-0 radial-fade" />
          <div className="relative">
            <h2 className="font-heading text-3xl lg:text-5xl font-extrabold tracking-tight mb-5">Ship your idea today</h2>
            <p className="text-slate-400 max-w-xl mx-auto mb-8">Join thousands building production apps with an AI team that never sleeps.</p>
            <button data-testid="cta-start-button" onClick={() => navigate("/signup")}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 transition-colors font-semibold">
              Start Building Free <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo size="sm" />
          <p className="font-mono text-xs text-slate-500">© 2026 NEXORA AI — Build at the speed of thought.</p>
        </div>
      </footer>
    </div>
  );
}

function PricingPreview({ navigate, user }) {
  const [annual, setAnnual] = useState(false);
  const plans = [
    { name: "Free Developer", price: 0, yr: 0, feats: ["3 projects", "50K AI tokens / mo", "Live preview", "Code export"], hl: false },
    { name: "Pro Founder", price: 29, yr: 278, feats: ["Unlimited projects", "2M AI tokens / mo", "All 12 AI agents", "1-click deploy", "Custom domains"], hl: true },
    { name: "Business", price: 99, yr: 950, feats: ["Everything in Pro", "10M AI tokens / mo", "Team collaboration", "Staging environments"], hl: false },
  ];
  return (
    <section id="pricing" className="max-w-7xl mx-auto px-6 py-20">
      <div className="text-center mb-10">
        <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">Simple, scalable pricing</h2>
        <div className="inline-flex items-center gap-3 mt-6 glass rounded-full p-1">
          <button onClick={() => setAnnual(false)} className={`px-4 py-1.5 rounded-full text-sm transition-colors ${!annual ? "bg-indigo-600" : "text-slate-400"}`}>Monthly</button>
          <button onClick={() => setAnnual(true)} className={`px-4 py-1.5 rounded-full text-sm transition-colors ${annual ? "bg-indigo-600" : "text-slate-400"}`}>Annual <span className="text-emerald-400">-20%</span></button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
        {plans.map((p) => (
          <div key={p.name} className={`glass rounded-2xl p-7 card-hover relative ${p.hl ? "border-indigo-500/50 glow-sm" : ""}`}>
            {p.hl && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-mono px-3 py-1 rounded-full bg-indigo-600">Most popular</span>}
            <h3 className="font-heading text-lg font-semibold">{p.name}</h3>
            <div className="mt-4 mb-6">
              <span className="font-heading text-4xl font-extrabold">${annual ? Math.round(p.yr / 12) : p.price}</span>
              <span className="text-slate-500 text-sm">/mo</span>
            </div>
            <ul className="space-y-3 mb-7">
              {p.feats.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-slate-300">
                  <Check className="h-4 w-4 text-emerald-400 shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <button onClick={() => navigate(user ? "/billing" : "/signup")}
              className={`w-full py-2.5 rounded-xl font-semibold text-sm transition-colors ${p.hl ? "bg-indigo-600 hover:bg-indigo-500" : "bg-white/[0.05] hover:bg-white/[0.08]"}`}>
              Get started
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="glass rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-5 text-left">
        <span className="font-medium">{q}</span>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-5 text-slate-400 leading-relaxed">{a}</div>}
    </div>
  );
}
